import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Chip, Paper, TextField } from '@mui/material';
import Autocomplete, {
  AutocompleteRenderInputParams,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';

import lang from '../../lib/language';
import { getPhoneNumberValidity, validateControl } from '../../lib/util/ValidationSchemes/Validate';
import { ControlError } from '../../lib/util/ValidationSchemes/ValidationTypes';

import { MobileCountryCodeSelector } from '../../containers/Verification/Verification.constants';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';
import Country from '../Country/Country';
import { ICountryControlOptions } from '../CountryControl/CountryControl.types';
import { InputFieldTypesEnum } from '../InputControl/Input.types';
import InputControl from '../InputControl/InputControl';

import { PhoneAutocompleteValue } from './PhoneAutocomplete.types';

import styles from './PhoneAutocomplete.module.scss';

type Props = {
  options: ICountryControlOptions[];
  value: PhoneAutocompleteValue;
  isClickedBtnContinue: boolean;
  onChange: (value: PhoneAutocompleteValue) => void;
};

const useStyles = makeStyles((theme) => ({
  option: {
    backgroundColor: '#191C2E',
    '&:hover': {
      backgroundColor: '#191C2E !important',
      color: '#191C2E !important',
    },
    '&[aria-disabled="true"]': {
      backgroundColor: '#3BB0FF80',
      color: '#FFB871 !important',
    },
  },
}));

const PhoneAutocomplete = (props: Props) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const { options, onChange, value, isClickedBtnContinue } = props;

  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [isShowCountryCode, setIsShowCountryCode] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<any>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const classes = useStyles();
  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  const [controlValue, setControlValue] = useState<PhoneAutocompleteValue>(
    value ?? {
      code: '',
      phone: '',
      countryId: '',
      label: '',
    },
  );

  const filterOptionsLetters: (options, state) => ICountryControlOptions[] = createFilterOptions({
    matchFrom: 'start',
    stringify: (option) => option.label,
  });

  const filterOptionsNumbers: (options, state) => ICountryControlOptions[] = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.value as string,
  });

  const onCountryChange = useCallback(
    (country): void => {
      const newValue = {
        ...controlValue,
        code: country.value,
        countryId: country.countryId,
      };
      setControlValue(newValue);
      onChange(newValue);
    },
    [controlValue, onChange],
  );

  const onPhoneNumberChange = (event: ChangeEvent<HTMLInputElement>): boolean => {
    const initialValue = event.target.value;
    const strippedValue = initialValue.replace(/[^0-9]/g, '');

    if (initialValue !== strippedValue) {
      event.target.value = strippedValue;
      return false;
    }
    const newValue = {
      ...controlValue,
      phone: initialValue,
    };
    setControlValue(newValue);
    onChange(newValue);
    return true;
  };

  useEffect(() => {
    if (options.length > 0 && value) {
      const optionToEmit: any = [];
      if (controlValue.code.toString().trim() !== '') {
        const filteredOptions = options.find(
          (option: ICountryControlOptions) =>
            option.value.toString().toLowerCase() === controlValue.code.toString().toLowerCase(),
        );
        optionToEmit.push(filteredOptions);
      } else {
        optionToEmit.push(options[0]);
      }
      setSelectedOption(optionToEmit);
    }
  }, [controlValue, options, value]);

  useEffect(() => {
    if (!isInitialised && (value?.code || value?.phone)) {
      setControlValue(value);
      setIsInitialised(true);
    }
  }, [isInitialised, value]);

  const handleClickOutside = () => {
    setIsShowCountryCode(true);

    if (controlValue.code.toString().trim() !== '') {
      const optionToEmit: any = [];
      const filteredOptions = options.find(
        (option: ICountryControlOptions) =>
          option.value.toString().toLowerCase() === controlValue.code.toString().toLowerCase(),
      );
      optionToEmit.push(filteredOptions);
      setSelectedOption(optionToEmit);
    }
  };

  const handleOnChange = (newValue) => {
    setIsShowCountryCode(true);

    requestAnimationFrame(() => {
      autocompleteInputRef.current?.blur();
    });

    if (newValue.length > 0) {
      const selected = newValue[newValue.length - 1];
      setSelectedOption([selected]);
      onCountryChange(selected);
    } else {
      setSelectedOption([]);
    }
  };

  const codeCheck = validateControl(
    value.code,
    [],
    lang.commonOnboardingPhoneVerificationCountry(),
    true,
  );

  const handleCountryCodeClick = () => {
    if (isMobile) {
      const selectedCountryOption = options.find(
        (option: ICountryControlOptions) => option.countryId === controlValue.countryId,
      )
        ?? options.find(
          (option: ICountryControlOptions) =>
            option.value.toString() === controlValue.code.toString(),
        );

      navigate(UI_ROUTES.searchCountryCode, {
        state: {
          [MobileCountryCodeSelector]: selectedCountryOption,
        },
      });
      return;
    }

    setIsShowCountryCode(false);
    setIsDropdownOpen(true);
  };

  return (
    <div className={classNames(styles.wrapper, { [styles.wrapperFullWidth]: true })}>
      <div className={styles.countryControl}>
        <div className={styles.phoneControl}>
          <div className={styles.wrapperPhoneControlContent}>
            <div className={styles.flag}>
              <Country
                id={
                  selectedOption.length > 0
                    ? selectedOption[selectedOption.length - 1].countryId
                    : controlValue.countryId
                }
              />
            </div>
            <div className={styles.phoneCode}>
              <div className={styles.header}>{lang.commonOnboardinghoneCountryCode()}</div>
              <Autocomplete
                multiple
                autoHighlight
                openOnFocus
                disableClearable
                value={selectedOption}
                onChange={(_, newValue) => handleOnChange(newValue)}
                options={options}
                classes={{
                  option: classes.option,
                }}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-noOptions': {
                        color: '#f2f4f7',
                      },
                    },
                  },
                }}
                filterOptions={(option: ICountryControlOptions[], state) => {
                  const patternOnlyStrings = /[a-zA-Z]/;
                  if (patternOnlyStrings.test(state.inputValue)) {
                    return filterOptionsLetters(option, state);
                  }
                  return filterOptionsNumbers(option, state);
                }}
                onOpen={handleCountryCodeClick}
                onClose={() => {
                  setIsDropdownOpen(false);
                }}
                PaperComponent={({ children }) => (
                  <Paper className={styles.paper}>{children}</Paper>
                )}
                renderOption={(itemProps, option) => (
                  <li {...itemProps}>
                    <Country
                      id={option.countryId ?? (option.value as string)}
                      phoneCode={option.value}
                    />
                  </li>
                )}
                getOptionLabel={(option) => option.value}
                getOptionDisabled={(option) => {
                  if (
                    selectedOption.length > 0
                    && selectedOption[0].countryId === option.countryId
                  ) {
                    return true;
                  }
                  return false;
                }}
                onBlur={handleClickOutside}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const countryCode = option.value.replace(/[^+0-9]/g, '');
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        id="countryCode"
                        aria-label="country code"
                        key={key}
                        deleteIcon={<></>}
                        label={countryCode}
                        {...tagProps}
                        onDelete={() => {}}
                        sx={{
                          color: '#fff',
                          position: 'absolute',
                          left: '55%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          margin: '0 !important',
                          padding: '0 !important',
                          zIndex: 2,
                          height: 'auto !important',
                          background: 'transparent !important',
                          border: 'none !important',
                        }}
                      />
                    );
                  })}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    inputRef={autocompleteInputRef}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      endAdornment: (
                        <>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: `translateY(-50%) rotate(${isDropdownOpen ? 180 : 0}deg)`,
                              zIndex: 3,
                              pointerEvents: 'none',
                              transition: 'transform 0.3s ease',
                            }}
                          >
                            <path
                              d="M7 10L12 15L17 10"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    className={classNames(
                      isShowCountryCode
                        ? styles.showInputControlCountry
                        : styles.hideInputControlCountry,
                      {
                        [styles.inputControlDark]: true,
                      },
                    )}
                    label={lang.commonOnboardingPersonalDetailsCountry()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                      width: 120,
                    }}
                  />
                )}
              />
            </div>
          </div>
          {isClickedBtnContinue
            && codeCheck.errors.length > 0
            && codeCheck.errors.map((error: ControlError) => (
              <p key={error.id} className={styles.error}>
                {error.error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.inputControl}>
        <div className={styles.header}>{lang.commonOnboardingPhoneVerificationPhoneNumber()}</div>
        <InputControl
          name=""
          placeholder=""
          id="phoneNumber"
          ariaLabel="phone number"
          value={controlValue.phone}
          onChange={onPhoneNumberChange}
          type={InputFieldTypesEnum.Number}
          label={lang.commonOnboardingPhoneVerificationEnterPhoneNumber()}
          errors={isClickedBtnContinue ? getPhoneNumberValidity(controlValue.phone).errors : []}
        />
      </div>
    </div>
  );
};

export default PhoneAutocomplete;
